
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info } from "lucide-react";
import { CSVImportForm } from "./import/CSVImportForm";
import { TemplateDownload } from "./import/TemplateDownload";
import { FieldMapper } from "./import/FieldMapper";
import { ImportPreview } from "./import/ImportPreview";
import { ImportResults } from "./import/ImportResults";
import { useImportProcess } from "./import/useImportProcess";
import type { ImportDataType } from "./types";

export function DataImport() {
  const [activeDataType, setActiveDataType] = useState<ImportDataType>("clients");
  const {
    csvData,
    headers,
    mappedFields,
    isImporting,
    importResults,
    importStep,
    handleCSVUpload,
    handleFieldMapping,
    handleImport,
    resetImport
  } = useImportProcess(activeDataType);

  return (
    <div className="space-y-6">
      <Tabs 
        value={activeDataType} 
        onValueChange={(value) => {
          setActiveDataType(value as ImportDataType);
          resetImport();
        }}
        className="w-full"
      >
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="clients">Clients</TabsTrigger>
          <TabsTrigger value="candidates">Candidates</TabsTrigger>
        </TabsList>

        <Alert className="mb-6">
          <Info className="h-4 w-4" />
          <AlertTitle>Import Guide</AlertTitle>
          <AlertDescription>
            Download our CSV template, fill it with your data, then upload it. 
            You'll have a chance to review and map fields before confirming the import.
          </AlertDescription>
        </Alert>

        <TabsContent value="clients" className="space-y-6">
          {importStep === "upload" && (
            <div className="space-y-6">
              <TemplateDownload dataType="clients" />
              <CSVImportForm 
                onDataUploaded={handleCSVUpload} 
                dataType="clients"
              />
            </div>
          )}
          
          {importStep === "map" && headers.length > 0 && (
            <FieldMapper 
              headers={headers} 
              dataType="clients" 
              onMapping={handleFieldMapping} 
              onBack={resetImport}
            />
          )}
          
          {importStep === "preview" && (
            <ImportPreview
              data={csvData}
              mappedFields={mappedFields}
              dataType="clients"
              onBack={() => resetImport()}
              onConfirm={handleImport}
              isImporting={isImporting}
            />
          )}
          
          {importStep === "complete" && (
            <ImportResults 
              results={importResults}
              onReset={resetImport}
              dataType="clients"
            />
          )}
        </TabsContent>

        <TabsContent value="candidates" className="space-y-6">
          {importStep === "upload" && (
            <div className="space-y-6">
              <TemplateDownload dataType="candidates" />
              <CSVImportForm 
                onDataUploaded={handleCSVUpload}
                dataType="candidates"
              />
            </div>
          )}
          
          {importStep === "map" && headers.length > 0 && (
            <FieldMapper 
              headers={headers} 
              dataType="candidates" 
              onMapping={handleFieldMapping}
              onBack={resetImport}
            />
          )}
          
          {importStep === "preview" && (
            <ImportPreview
              data={csvData}
              mappedFields={mappedFields}
              dataType="candidates"
              onBack={() => resetImport()}
              onConfirm={handleImport}
              isImporting={isImporting}
            />
          )}
          
          {importStep === "complete" && (
            <ImportResults 
              results={importResults}
              onReset={resetImport}
              dataType="candidates"
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
